"use client";

import { Box, HStack, Tag, Text } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { BillingPeriod } from "../data/pricingData";

interface Props {
  billing: BillingPeriod;
  setBilling: (period: BillingPeriod) => void;
}

export default function BillingToggle({ billing, setBilling }: Props) {
  const t = useTranslations("pricing.common");

  return (
    <HStack
      role="group"
      aria-label={t("billingToggleLabel")}
      bg="neutral.100"
      p={1}
      borderRadius="full"
      borderWidth="1px"
      borderColor="neutral.200"
      boxShadow="sm"
      gap={1}
    >
      {(["monthly", "annual"] as const).map((period) => {
        const isActive = billing === period;
        return (
          <Box
            key={period}
            as="button"
            onClick={() => setBilling(period)}
            px={{ base: 5, md: 4 }}
            py={1.5}
            borderRadius="full"
            fontWeight="semibold"
            textStyle="sm"
            transition="all 0.2s ease"
            bg={isActive ? "primary.500" : "transparent"}
            color={isActive ? "white" : "neutral.700"}
            boxShadow={isActive ? "md" : "none"}
            _hover={{
              bg: isActive ? "primary.600" : "neutral.200",
              color: isActive ? "white" : "neutral.900",
              cursor: "pointer",
            }}
            _focusVisible={{
              outline: "2px solid",
              outlineColor: "primary.400",
              outlineOffset: "2px",
            }}
          >
            <HStack gap={2} justify="center">
              <Text as="span" color="inherit">
                {period === "monthly" ? t("monthly") : t("yearly")}
              </Text>
              {period === "annual" ? (
                <Tag.Root
                  size="sm"
                  colorPalette="green"
                  variant={isActive ? "solid" : "subtle"}
                  borderRadius="full"
                >
                  <Tag.Label textStyle="2xs" fontWeight="bold">
                    {t("savePercent")}
                  </Tag.Label>
                </Tag.Root>
              ) : null}
            </HStack>
          </Box>
        );
      })}
    </HStack>
  );
}
